import { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { apiGet } from '../../lib/api';
import { Card } from '../../components/Card';
import { PageScaffold } from '../../components/PageScaffold';
import { Money } from '../../components/Money';
import { StatusPill } from '../../components/StatusPill';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ProductPlaceholder } from '../../components/ProductPlaceholder';
import { cn } from '../../lib/cn';
import { useAuth } from '../../lib/auth';
import { useCart } from '../../lib/cart';

interface Variant {
  id: string;
  size: string | null;
  basePrice: string;
  salePrice: string | null;
  onHand?: number;
}
interface CatalogueModel {
  id: string;
  name: string;
  description: string | null;
  gift: string | null;
  gifts?: { qty: number; name: string }[];
  category: { id: string; name: string; code: string; fulfillmentStrategy: string };
  colorOptions: string[];
  sideColorOptions: string[];
  isActive: boolean;
  image: string | null;
  variantCount: number;
  sizes: (string | null)[];
  variants: Variant[];
  priceFrom: string | null;
  priceTo: string | null;
  // Stock-tracked categories (STOCK_AT_LOCATION) carry a
  // total so the card can show "Out of stock". Made-to-order categories pass
  // null — they're always orderable.
  stockTracked?: boolean;
  totalStock?: number | null;
}

interface Category {
  id: string;
  name: string;
  code: string;
  fulfillmentStrategy: string;
}

type Sort = 'featured' | 'price_asc' | 'price_desc' | 'name';

const strategyLabel = (s: string): string =>
  ({
    STOCK_AT_LOCATION: 'In stock',
    MADE_TO_ORDER_FACTORY: 'Made to order',
    MADE_TO_ORDER_SUPPLIER: 'From supplier',
  })[s] ?? s;

const strategyTone = (s: string): 'positive' | 'info' | 'warning' | 'accent' => {
  switch (s) {
    case 'STOCK_AT_LOCATION':
      return 'positive';
    case 'MADE_TO_ORDER_FACTORY':
      return 'info';
    case 'MADE_TO_ORDER_SUPPLIER':
      return 'warning';
    default:
      return 'accent';
  }
};

const SWATCH: Record<string, string> = {
  blue: '#2f5b8f',
  brown: '#6b4a2b',
  red: '#9b2d2d',
  grena: '#6e1b2f',
  gris: '#8a8a8a',
  blanc: '#f3f1ec',
  white: '#f3f1ec',
  black: '#222',
  noir: '#222',
  beige: '#d8cdb8',
};
const swatch = (c: string): string => SWATCH[c.toLowerCase()] ?? '#bbb';

const vPrice = (v: Variant): number => Number(v.salePrice ?? v.basePrice);

// Sort sizes naturally: "90*190" < "140*190" < "200*200"; non-dimensional last.
function sizeCompare(a: string, b: string): number {
  const pa = a.match(/(\d+)\D+(\d+)/);
  const pb = b.match(/(\d+)\D+(\d+)/);
  if (pa && pb) return Number(pa[1]) - Number(pb[1]) || Number(pa[2]) - Number(pb[2]);
  if (pa) return -1;
  if (pb) return 1;
  return a.localeCompare(b);
}

// Price to display on a card given the active size filter.
function cardPrice(
  m: CatalogueModel,
  size: string,
): { value: number | null; exact: boolean; base: number | null; onSale: boolean } {
  const pool = (size ? m.variants.filter((v) => v.size === size) : m.variants).filter(
    (v) => Number(v.basePrice) > 0,
  );
  if (pool.length === 0) return { value: null, exact: !!size, base: null, onSale: false };
  if (size) {
    const v = pool[0]!;
    return { value: vPrice(v), exact: true, base: Number(v.basePrice), onSale: !!v.salePrice };
  }
  const min = Math.min(...pool.map(vPrice));
  return { value: min, exact: false, base: null, onSale: pool.some((v) => v.salePrice) };
}

const SORTS: Sort[] = ['featured', 'price_asc', 'price_desc', 'name'];

export function CataloguePage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const category = params.get('category') ?? '';
  const size = params.get('size') ?? '';
  const priceMin = params.get('min') ?? '';
  const priceMax = params.get('max') ?? '';
  const sortParam = params.get('sort');
  const sort: Sort = SORTS.includes(sortParam as Sort) ? (sortParam as Sort) : 'featured';
  const onSaleOnly = params.get('promo') === '1';

  const setParam = useCallback(
    (key: string, value: string): void => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const setQ = (v: string): void => setParam('q', v);
  const setCategory = (v: string): void => setParam('category', v);
  const setSize = (v: string): void => setParam('size', v);
  const setPriceMin = (v: string): void => setParam('min', v);
  const setPriceMax = (v: string): void => setParam('max', v);
  const setSort = (v: Sort): void => setParam('sort', v === 'featured' ? '' : v);
  const setOnSaleOnly = (v: boolean): void => setParam('promo', v ? '1' : '');

  // Start a sale straight from the catalogue. Both targets seed from the shared
  // cart, so when items have been added the buttons read "… from cart".
  const { perms } = useAuth();
  const { totalUnits } = useCart();
  const canCreateOrder = perms.has('orders.create');
  const canCreateQuote = perms.has('quotations.create');

  const cats = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiGet<Category[]>('/categories'),
  });
  const models = useQuery({
    queryKey: ['product-models', 'all'],
    queryFn: () => apiGet<CatalogueModel[]>('/product-models'),
  });
  const all = useMemo(() => models.data ?? [], [models.data]);

  // Size options reflect the chosen category.
  const sizeOptions = useMemo(() => {
    const set = new Set<string>();
    all
      .filter((m) => !category || m.category.id === category)
      .forEach((m) => m.variants.forEach((v) => v.size && set.add(v.size)));
    return [...set].sort(sizeCompare);
  }, [all, category]);

  // Reset an out-of-range size when the category changes.
  useEffect(() => {
    if (size && !sizeOptions.includes(size)) setParam('size', '');
  }, [sizeOptions, size, setParam]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const min = priceMin === '' ? null : Number(priceMin);
    const max = priceMax === '' ? null : Number(priceMax);
    const list = all.filter((m) => {
      if (ql && !m.name.toLowerCase().includes(ql)) return false;
      if (category && m.category.id !== category) return false;
      if (size && !m.variants.some((v) => v.size === size)) return false;
      const cp = cardPrice(m, size);
      if (onSaleOnly && !cp.onSale) return false;
      if (min != null && (cp.value == null || cp.value < min)) return false;
      if (max != null && (cp.value == null || cp.value > max)) return false;
      return true;
    });
    const priceOf = (m: CatalogueModel): number =>
      cardPrice(m, size).value ?? Number.POSITIVE_INFINITY;
    if (sort === 'name') list.sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === 'price_asc') list.sort((a, b) => priceOf(a) - priceOf(b));
    else if (sort === 'price_desc') list.sort((a, b) => priceOf(b) - priceOf(a));
    return list;
  }, [all, q, category, size, priceMin, priceMax, sort, onSaleOnly]);

  const hasFilters = !!(
    q ||
    category ||
    size ||
    priceMin ||
    priceMax ||
    onSaleOnly ||
    sort !== 'featured'
  );
  const clear = (): void => setParams({}, { replace: true });

  return (
    <PageScaffold
      title="Catalogue"
      subtitle={
        <>
          {filtered.length} of {all.length} model{all.length === 1 ? '' : 's'}
          {size && (
            <>
              {' '}
              · showing <span className="text-ink">{size}</span> pricing
            </>
          )}
        </>
      }
      actions={
        <div className="flex items-center gap-2">
          {totalUnits > 0 && (canCreateOrder || canCreateQuote) && (
            <span className="text-xs text-ink-2 bg-accent-soft border border-accent/30 rounded-full px-2.5 py-1 whitespace-nowrap">
              🛒 {totalUnits} in cart
            </span>
          )}
          {canCreateOrder && (
            <Link to="/orders/new">
              <Button>{totalUnits > 0 ? 'Order from cart →' : '+ New order'}</Button>
            </Link>
          )}
          {canCreateQuote && (
            <Link to="/quotations/new">
              <Button variant="secondary">
                {totalUnits > 0 ? 'Quote from cart →' : '+ New quotation'}
              </Button>
            </Link>
          )}
        </div>
      }
    >
      {/* Filter bar */}
      <Card bodyClassName="p-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <label className="md:col-span-4 flex flex-col gap-1" htmlFor="search">
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Search</span>
            <div className="relative">
              <svg
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-4"
              >
                <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6" />
                <path d="M14 14l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
              <input
                id="search"
                type="text"
                placeholder="Model name…"
                autoComplete="off"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape' && q) setQ('');
                }}
                className="kc-input w-full pl-8 pr-8"
              />
              {q && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setQ('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded text-ink-4 hover:text-ink hover:bg-paper-2 transition"
                >
                  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="w-3.5 h-3.5">
                    <path
                      d="M5 5l10 10M15 5L5 15"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              )}
            </div>
          </label>
          <FilterSelect
            label="Category"
            value={category}
            onChange={setCategory}
            className="md:col-span-3"
          >
            <option value="">All categories</option>
            {(cats.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect label="Size" value={size} onChange={setSize} className="md:col-span-2">
            <option value="">All sizes</option>
            {sizeOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect
            label="Sort"
            value={sort}
            onChange={(v) => setSort(v as Sort)}
            className="md:col-span-3"
          >
            <option value="featured">Featured</option>
            <option value="price_asc">Price: low → high</option>
            <option value="price_desc">Price: high → low</option>
            <option value="name">Name A–Z</option>
          </FilterSelect>

          <div className="md:col-span-4">
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
              Price (Rs)
            </span>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="number"
                min={0}
                placeholder="min"
                value={priceMin}
                onChange={(e) => setPriceMin(e.target.value)}
                className="kc-input kc-input-sm w-full"
              />
              <span className="text-ink-4">–</span>
              <input
                type="number"
                min={0}
                placeholder="max"
                value={priceMax}
                onChange={(e) => setPriceMax(e.target.value)}
                className="kc-input kc-input-sm w-full"
              />
            </div>
          </div>
          <label className="md:col-span-3 flex items-center gap-2 text-sm pb-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={onSaleOnly}
              onChange={(e) => setOnSaleOnly(e.target.checked)}
            />
            <span className="text-ink-2">On promo only</span>
          </label>
          <div className="md:col-span-5 flex justify-end pb-0.5">
            {hasFilters && (
              <Button variant="tertiary" size="xs" onClick={clear}>
                Clear all filters
              </Button>
            )}
          </div>
        </div>
      </Card>

      {models.isLoading ? (
        <div className="text-center text-ink-3 py-12">Loading…</div>
      ) : filtered.length === 0 ? (
        <Card>
          <EmptyState
            title="No models match"
            description="Try widening the price range or clearing a filter."
            action={
              hasFilters ? (
                <Button variant="secondary" size="sm" onClick={clear}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((m) => (
            <ModelCard key={m.id} m={m} size={size} backSearch={params.toString()} />
          ))}
        </div>
      )}
    </PageScaffold>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  className,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  className?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className={cn('flex flex-col gap-1', className)}>
      <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="kc-input">
        {children}
      </select>
    </label>
  );
}

function ModelCard({
  m,
  size,
  backSearch,
}: {
  m: CatalogueModel;
  size: string;
  backSearch: string;
}): JSX.Element {
  const hasColors = m.colorOptions.length > 0;
  const cp = cardPrice(m, size);
  // Size-aware out-of-stock: if a specific size is selected, look at that
  // variant's onHand; otherwise fall back to the model-wide totalStock.
  const sizeVariant = size ? m.variants.find((v) => v.size === size) : null;
  const sizeOnHand = sizeVariant?.onHand;
  const outOfStock =
    m.stockTracked === true && (size ? sizeOnHand != null && sizeOnHand === 0 : m.totalStock === 0);
  return (
    <Link
      to={size ? `/catalogue/${m.id}?size=${encodeURIComponent(size)}` : `/catalogue/${m.id}`}
      state={{ backSearch }}
      className={cn(
        'group bg-surface border border-rule rounded-lg overflow-hidden hover:border-accent/40 hover:shadow-md transition flex flex-col focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50',
        outOfStock && 'opacity-80',
      )}
    >
      <div className="aspect-[4/3] bg-paper-2 relative overflow-hidden">
        {m.image ? (
          <img
            src={m.image}
            alt={m.name}
            className={cn(
              'w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300',
              outOfStock && 'grayscale',
            )}
          />
        ) : (
          <ProductPlaceholder />
        )}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
          <StatusPill tone={strategyTone(m.category.fulfillmentStrategy)}>
            {strategyLabel(m.category.fulfillmentStrategy)}
          </StatusPill>
          {outOfStock && <StatusPill tone="negative">Out of stock</StatusPill>}
          {(m.gift || (m.gifts?.length ?? 0) > 0) && <StatusPill tone="accent">🎁 Gift</StatusPill>}
        </div>
        {cp.onSale && !outOfStock && (
          <div className="absolute top-3 right-3">
            <StatusPill tone="accent">Promo</StatusPill>
          </div>
        )}
        <div className="absolute bottom-3 right-3 bg-ink/60 text-paper-2 text-[10px] kc-mono px-1.5 py-0.5 rounded">
          {size ? size : `${m.variantCount} size${m.variantCount === 1 ? '' : 's'}`}
        </div>
      </div>
      <div className="px-4 pt-4 flex-1 flex flex-col">
        <div className="text-[10px] uppercase tracking-wider text-ink-3">{m.category.name}</div>
        <h3 className="mt-1 text-sm font-medium text-ink leading-snug group-hover:text-accent line-clamp-2">
          {m.name}
        </h3>
        {hasColors && (
          <div className="flex items-center gap-1 mt-2">
            {m.colorOptions.slice(0, 5).map((c) => (
              <span
                key={c}
                title={c}
                className="w-3.5 h-3.5 rounded-full border border-rule"
                style={{ backgroundColor: swatch(c) }}
              />
            ))}
            <span className="text-[10px] text-ink-3 ml-1">{m.colorOptions.length} colours</span>
          </div>
        )}
        <div className="mt-auto pt-3">
          {cp.value != null ? (
            <div className="flex items-baseline gap-1.5">
              {!cp.exact && (
                <span className="text-[10px] text-ink-3 uppercase tracking-wide">from</span>
              )}
              <Money
                value={String(cp.value)}
                className={cn('text-lg font-medium', outOfStock ? 'text-ink-3' : 'text-ink')}
              />
              {cp.exact && cp.onSale && cp.base != null && (
                <Money value={String(cp.base)} className="text-xs text-ink-4 line-through" />
              )}
            </div>
          ) : (
            <span className="text-sm text-ink-3 italic">Price on request</span>
          )}
        </div>
      </div>
    </Link>
  );
}
